import React, { useState, useMemo } from 'react'
import { Nav, Dropdown } from 'react-bootstrap'
import {
  Gauge,
  Award,
  Rocket,
  AlertTriangle,
  Search,
  Filter,
  Grid3X3,
  List,
  Plus,
  SortAsc,
  SortDesc,
  Calendar,
  Activity,
} from 'lucide-react'
import {
  ViewContainer,
  TabsContainer,
  CustomNavTabs,
  TabContent,
  MainContent,
  QuickStatsBar,
  StatGroup,
  QuickStat,
  ToolbarSection,
  ToolbarLeft,
  ToolbarRight,
  SearchInput,
  SearchWrapper,
  ToolbarButton,
  CreateButton,
  FilterBadge,
  ModelsContent,
  ContentHeader,
  GalleryContainer,
} from './styles'
import ModelsGallery from './ModelsGallery'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import {
  setSelectedModelId,
  selectSelectedModelId,
  clearSelectedModelId,
} from '../../../store/slices/modelSlice'
import { useFetchModelsQuery, useCreateModelMutation } from '../../../services/modelsApi'
import { Model } from '../../../types/models/model'
import { rateBBoxModel, rateClassificationModel } from '../../../utils'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { NoModelsCard, ModelDetailSidebar } from './components'
import NewModelModal from '../../../components/modals/NewModelModal'
import ErrorDialog from '../../../components/dialogs/ErrorDialog'
import { adjectives, nouns } from '../../../utils/modelNameWords'
import { useNavigate } from 'react-router-dom'
import { ObjectDetectionHyperparameters } from '../../../types/models/hyperparameters'

const CustomDropdownToggle = React.forwardRef<
  HTMLButtonElement,
  { children: React.ReactNode; onClick?: (e: React.MouseEvent) => void }
>(({ children, onClick }, ref) => (
  <ToolbarButton
    ref={ref}
    onClick={onClick}
    style={{
      position: 'relative',
    }}
  >
    {children}
    <span
      style={{
        marginLeft: '0.5rem',
        display: 'inline-block',
        borderTop: '0.3em solid',
        borderRight: '0.3em solid transparent',
        borderBottom: '0',
        borderLeft: '0.3em solid transparent',
      }}
    />
  </ToolbarButton>
))

CustomDropdownToggle.displayName = 'CustomDropdownToggle'

const ModelsOverview: React.FC = () => {
  type ViewMode = 'grid' | 'list'
  type SortBy = 'name' | 'created' | 'status' | 'performance'
  type SortOrder = 'asc' | 'desc'
  type StatusFilter = 'all' | 'trained' | 'training' | 'deployed' | 'error'

  interface ModelStats {
    total: number
    trained: number
    deployed: number
    error: number
    bestModel: Model | null
    worstModel: Model | null
    averageModel: Model | null
  }

  const [showNewModelModal, setShowNewModelModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterText, setFilterText] = useState('')
  const [selectedBaseModel, setSelectedBaseModel] = useState<{
    id: string
    name: string
  } | null>(null)

  // New state for enhanced functionality
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [sortBy, setSortBy] = useState<SortBy>('created')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  // Model details sidebar state
  const [selectedModelForDetail, setSelectedModelForDetail] = useState<Model | null>(null)
  const [showDetailSidebar, setShowDetailSidebar] = useState(false)

  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    {
      skip: !selectedProjectId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      pollingInterval: 60000,
    }
  )
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const [createModel] = useCreateModelMutation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const baseModels = [{ id: 'baseModel1', name: 'AG Base', image: '/agileview-eye.png' }]

  const handleCreateModel = () => {
    setShowNewModelModal(true)
  }

  const generateModelName = () => {
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]
    return `${randomAdjective}-${randomNoun}`
  }

  const handleGenerateNewModel = async () => {
    if (selectedBaseModel && selectedProjectId) {
      try {
        const newModelName = generateModelName()
        const newModel = await createModel({
          project_id: selectedProjectId,
          name: newModelName,
          description: '',
        }).unwrap()

        await new Promise((resolve) => setTimeout(resolve, 100))

        dispatch(
          setSelectedModelId({
            modelId: newModel.id,
            projectId: selectedProjectId || '',
          })
        )

        navigate(`/models/${selectedProjectId}/training`)

        setShowNewModelModal(false)
        setSelectedBaseModel(null)
        setFilterText('')
      } catch (err: unknown) {
        const message =
          typeof err === 'object' &&
          err !== null &&
          'data' in err &&
          typeof (err as { data?: { message?: unknown } }).data?.message === 'string'
            ? ((err as { data?: { message?: string } }).data?.message as string)
            : 'Failed to create new model'
        setError(message)
      }
    }
  }

  const calculateModelStats = (): ModelStats => {
    if (!modelsData?.models || !project)
      return {
        total: 0,
        trained: 0,
        deployed: 0,
        error: 0,
        bestModel: null,
        worstModel: null,
        averageModel: null,
      }

    const models = modelsData.models
    const trained = models.filter((m) => m.state.toLowerCase() === 'trained').length
    const deployed = models.filter(
      (m) => m.deployment?.status.toLowerCase() === 'in_service'
    ).length
    const error = models.filter(
      (m) => m.state.toLowerCase() === 'err' || m.deployment?.status.toLowerCase() === 'err'
    ).length

    const modelRatings = models.map((model) => {
      const rating =
        project.annotation_type === 'classification'
          ? rateClassificationModel(model).classificationRating
          : rateBBoxModel(model).bBoxRating

      return { model, rating }
    })

    modelRatings.sort((a, b) => b.rating - a.rating)
    const bestModel = modelRatings[0]?.model || null
    const worstModel = modelRatings[modelRatings.length - 1]?.model || null

    const averageModel =
      models.length > 0
        ? {
            ...models[0],
            metrics: Object.keys(models[0].metrics || {}).reduce((acc, key) => {
              const sum = models.reduce((sum, model) => {
                const value = model.metrics?.[key]
                return sum + (typeof value === 'number' ? value : 0)
              }, 0)
              acc[key] = sum / models.length
              return acc
            }, {} as Record<string, unknown>),
          }
        : null

    return {
      total: models.length,
      trained,
      deployed,
      error,
      bestModel,
      worstModel,
      averageModel,
    }
  }

  // Filter and sort models
  const filteredAndSortedModels = useMemo(() => {
    if (!modelsData?.models) return []

    let filtered = modelsData.models

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((model) =>
        model.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((model) => {
        switch (statusFilter) {
          case 'trained':
            return model.state.toLowerCase() === 'trained'
          case 'training':
            return model.state.toLowerCase() === 'training'
          case 'deployed':
            return model.deployment?.status.toLowerCase() === 'in_service'
          case 'error':
            return (
              model.state.toLowerCase() === 'err' ||
              model.deployment?.status.toLowerCase() === 'err'
            )
          default:
            return true
        }
      })
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'created':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
        case 'status':
          comparison = a.state.localeCompare(b.state)
          break
        case 'performance':
          if (project) {
            const ratingA =
              project.annotation_type === 'classification'
                ? rateClassificationModel(a).classificationRating
                : rateBBoxModel(a).bBoxRating
            const ratingB =
              project.annotation_type === 'classification'
                ? rateClassificationModel(b).classificationRating
                : rateBBoxModel(b).bBoxRating
            comparison = ratingA - ratingB
          }
          break
      }

      return sortOrder === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [modelsData?.models, searchQuery, statusFilter, sortBy, sortOrder, project])

  const stats = calculateModelStats()
  const activeFiltersCount = (searchQuery ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0)

  // Handle model details sidebar
  const handleModelDetailClick = (model: Model) => {
    setSelectedModelForDetail(model)
    setShowDetailSidebar(true)
  }

  const handleDetailSidebarClose = () => {
    setShowDetailSidebar(false)
    setSelectedModelForDetail(null)
  }

  const handleSelectNextModelAfterDelete = (deletedModelId: string) => {
    // If the deleted model was the currently selected model, select the newest remaining model
    const remainingModels = modelsData?.models.filter((m) => m.id !== deletedModelId)
    if (remainingModels && remainingModels.length > 0) {
      const newestModel = remainingModels.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0]
      dispatch(setSelectedModelId({ modelId: newestModel.id, projectId: selectedProjectId || '' }))
    }
  }

  const getDatasetInformation = (model: Model) => {
    // Get dataset stats for training and validation datasets
    const trainDatasets = model.parameters?.train_metadata?.datasource || []
    const validationDatasets = model.parameters?.validation_metadata?.datasource || []

    const hp = model.parameters?.hyperparameters
    const inner = hp?.hyperparameters

    const epochs = (inner?.training?.epochs?.value as number) || undefined

    let imageSize: number | undefined = undefined
    let augmentation = false

    if (hp?.type === 'object_detection') {
      const odTraining = (inner as ObjectDetectionHyperparameters | null | undefined)?.training
      imageSize = odTraining?.image_shape?.value as number | undefined
      augmentation = (odTraining?.augment?.value as boolean | undefined) ?? false
    }

    return {
      training: trainDatasets,
      validation: validationDatasets,
      epochs,
      imageSize,
      augmentation,
    }
  }

  const renderMyModelsContent = () => {
    if (!modelsData?.models || modelsData.models.length === 0) {
      return <NoModelsCard onCreateModel={handleCreateModel} />
    }

    return (
      <MainContent>
        {/* Quick Stats Bar */}
        <QuickStatsBar>
          <StatGroup>
            <QuickStat color="#553c9a">
              <Gauge className="icon" size={12} />
              <span className="value">{stats.total}</span>
              <span className="label">Total</span>
            </QuickStat>
            <QuickStat color="#276749">
              <Award className="icon" size={12} />
              <span className="value">{stats.trained}</span>
              <span className="label">Trained</span>
            </QuickStat>
            <QuickStat color="#285e61">
              <Rocket className="icon" size={12} />
              <span className="value">{stats.deployed}</span>
              <span className="label">Deployed</span>
            </QuickStat>
            {stats.error > 0 && (
              <QuickStat color="#9b2c2c">
                <AlertTriangle className="icon" size={12} />
                <span className="value">{stats.error}</span>
                <span className="label">Errors</span>
              </QuickStat>
            )}
          </StatGroup>
          <CreateButton onClick={handleCreateModel}>
            <Plus size={18} />
            New Model
          </CreateButton>
        </QuickStatsBar>

        {/* Toolbar */}
        <ToolbarSection>
          <ToolbarLeft>
            <SearchWrapper>
              <Search className="search-icon" size={16} />
              <SearchInput
                type="text"
                placeholder="Search models..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchWrapper>

            <Dropdown>
              <Dropdown.Toggle as={CustomDropdownToggle}>
                <Filter size={16} />
                Status
                {activeFiltersCount > 0 && <FilterBadge>{activeFiltersCount}</FilterBadge>}
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{ background: '#2a2a2a', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <Dropdown.Item
                  onClick={() => setStatusFilter('all')}
                  style={{ color: statusFilter === 'all' ? '#553c9a' : 'rgba(255,255,255,0.8)' }}
                >
                  All Models
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setStatusFilter('trained')}
                  style={{
                    color: statusFilter === 'trained' ? '#553c9a' : 'rgba(255,255,255,0.8)',
                  }}
                >
                  Trained
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setStatusFilter('training')}
                  style={{
                    color: statusFilter === 'training' ? '#553c9a' : 'rgba(255,255,255,0.8)',
                  }}
                >
                  Training
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setStatusFilter('deployed')}
                  style={{
                    color: statusFilter === 'deployed' ? '#553c9a' : 'rgba(255,255,255,0.8)',
                  }}
                >
                  Deployed
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setStatusFilter('error')}
                  style={{ color: statusFilter === 'error' ? '#553c9a' : 'rgba(255,255,255,0.8)' }}
                >
                  Error
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown>
              <Dropdown.Toggle as={CustomDropdownToggle}>
                {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
                Sort
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{ background: '#2a2a2a', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <Dropdown.Item
                  onClick={() => setSortBy('name')}
                  style={{ color: sortBy === 'name' ? '#553c9a' : 'rgba(255,255,255,0.8)' }}
                >
                  Name
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setSortBy('created')}
                  style={{ color: sortBy === 'created' ? '#553c9a' : 'rgba(255,255,255,0.8)' }}
                >
                  <Calendar size={14} style={{ marginRight: '0.5rem' }} />
                  Created Date
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setSortBy('performance')}
                  style={{ color: sortBy === 'performance' ? '#553c9a' : 'rgba(255,255,255,0.8)' }}
                >
                  <Activity size={14} style={{ marginRight: '0.5rem' }} />
                  Performance
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </ToolbarLeft>

          <ToolbarRight>
            <ToolbarButton
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortOrder === 'asc' ? <SortDesc size={16} /> : <SortAsc size={16} />}
            </ToolbarButton>

            <ToolbarButton
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <Grid3X3 size={16} />
            </ToolbarButton>

            <ToolbarButton
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={16} />
            </ToolbarButton>
          </ToolbarRight>
        </ToolbarSection>

        {/* Models Content */}
        <ModelsContent>
          <ContentHeader>
            <h2>My Models</h2>
            <div className="results-count">
              {filteredAndSortedModels.length} of {stats.total} models
            </div>
          </ContentHeader>

          <GalleryContainer>
            <ModelsGallery
              models={filteredAndSortedModels}
              viewMode={viewMode}
              project={project}
              selectedModelId={selectedModelId}
              onModelClick={handleModelDetailClick}
            />
          </GalleryContainer>
        </ModelsContent>
      </MainContent>
    )
  }

  return (
    <ViewContainer>
      {/* Tabs Navigation */}
      <TabsContainer>
        <CustomNavTabs variant="tabs" activeKey="myModels">
          <Nav.Item>
            <Nav.Link eventKey="myModels">My Models</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link disabled className="disabled">
              Catalog Models{' '}
              <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>(Coming Soon)</span>
            </Nav.Link>
          </Nav.Item>
        </CustomNavTabs>
      </TabsContainer>

      {/* Tab Content */}
      <TabContent>
        {renderMyModelsContent()}

        {/* Sidebar Overlay */}
        {selectedModelForDetail &&
          project &&
          showDetailSidebar &&
          (() => {
            const datasetInfo = getDatasetInformation(selectedModelForDetail)

            return (
              <ModelDetailSidebar
                show={showDetailSidebar}
                onClose={handleDetailSidebarClose}
                model={selectedModelForDetail}
                project={project}
                datasetInfo={datasetInfo}
                onDelete={handleSelectNextModelAfterDelete}
              />
            )
          })()}
      </TabContent>

      {/* Modals and Dialogs */}
      <NewModelModal
        show={showNewModelModal}
        onClose={() => {
          setShowNewModelModal(false)
          setSelectedBaseModel(null)
          setFilterText('')
        }}
        baseModels={baseModels}
        filterText={filterText}
        setFilterText={setFilterText}
        selectedBaseModel={selectedBaseModel}
        onBaseModelSelect={setSelectedBaseModel}
        onGenerateNewModel={handleGenerateNewModel}
      />
      <ErrorDialog show={!!error} onClose={() => setError(null)} message={error || ''} />
    </ViewContainer>
  )
}

export default ModelsOverview
